import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} GiftVoucherRecipientFormRefs
 * @property {HTMLInputElement} myEmailButton - Button for selecting my email option
 * @property {HTMLInputElement} recipientEmailButton - Button for selecting recipient email option
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLInputElement} recipientName - Recipient name input field
 * @property {HTMLInputElement} senderName - Sender name input field
 * @property {HTMLTextAreaElement} recipientMessage - Recipient message textarea
 * @property {HTMLInputElement} voucherAmountInput - Hidden input posting properties[_voucher_amount]
 * @property {HTMLInputElement} [otherAmountRadio] - "Other" amount radio (optional)
 * @property {HTMLDivElement} [customAmountField] - Custom amount field container (optional)
 * @property {HTMLInputElement} [customAmountInput] - Custom amount number input (optional)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLSpanElement} [characterCount] - Character count display element (optional)
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 */

/**
 * Recipient form for the AP21 gift voucher product (a normal product, not a
 * native Shopify gift card). The fields post as plain line item properties —
 * "Recipient email", "Recipient name", "Sender name", "Message" — which the
 * order export reads to have AP21 issue and email the voucher. Unlike the
 * native gift-card form there is no send-on date (AP21 emails on order
 * export) and no __shopify_send_gift_card_to_recipient control flag.
 *
 * @extends {Component<GiftVoucherRecipientFormRefs>}
 */
class GiftVoucherRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self', // Send to my email
    RECIPIENT: 'recipient_form', // Send to recipient's email with form
  };

  #currentMode = GiftVoucherRecipientForm.DeliveryMode.SELF;

  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {(() => void) | null} */
  #cartAddEventBound = null;

  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'recipientFields',
    'recipientEmail',
    'recipientName',
    'senderName',
    'recipientMessage',
    'voucherAmountInput',
  ];

  /**
   * Get all recipient input fields
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]} Array of input fields
   */
  get #inputFields() {
    return [this.refs.recipientEmail, this.refs.recipientName, this.refs.senderName, this.refs.recipientMessage];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#cartAddEventBound = () => this.#clearErrorState();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }
  }

  /**
   * Initialize form with default state, self delivery is selected by default
   */
  #initializeForm() {
    this.#updateButtonStates(GiftVoucherRecipientForm.DeliveryMode.SELF);
    this.refs.recipientFields.hidden = true;
    this.#clearRecipientFields();
    this.#disableRecipientFields();
  }

  /**
   * Handle a voucher amount choice: a preset radio posts its value straight to
   * the hidden `_voucher_amount` property input; the "Other" radio reveals the
   * custom amount field instead. The cart transform validates server-side and
   * falls back to the base price on anything invalid.
   * @param {Event} event - Change event from an amount radio
   */
  selectAmount(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const { customAmountField, customAmountInput } = this.refs;
    const isCustom = target.value === 'other';

    if (customAmountField) customAmountField.hidden = !isCustom;

    if (isCustom) {
      if (customAmountInput) {
        customAmountInput.disabled = false;
        customAmountInput.setAttribute('required', 'required');
        customAmountInput.focus();
      }
      this.#syncCustomAmount();
    } else {
      if (customAmountInput) {
        customAmountInput.disabled = true;
        customAmountInput.removeAttribute('required');
        customAmountInput.value = '';
      }
      this.refs.voucherAmountInput.value = target.value;
    }
  }

  /**
   * Keep the hidden property input in sync while the buyer types a custom
   * amount.
   */
  updateCustomAmount() {
    this.#syncCustomAmount();
  }

  #syncCustomAmount() {
    const { customAmountInput, voucherAmountInput } = this.refs;
    if (!customAmountInput) return;

    voucherAmountInput.value = customAmountInput.checkValidity() ? customAmountInput.value : '';
  }

  /**
   * Handle toggle between my email and recipient email
   * @param {string} mode - Delivery mode (either 'self' or 'recipient_form')
   * @param {Event} _event - Change event (unused)
   */
  toggleRecipientForm(mode, _event) {
    if (!Object.values(GiftVoucherRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftVoucherRecipientForm.DeliveryMode).join(
          ', '
        )}`
      );
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;

    this.#updateFormState();
  }

  /**
   * Update form state based on current mode
   */
  #updateFormState() {
    const { DeliveryMode } = GiftVoucherRecipientForm;
    const isRecipientMode = this.#currentMode === DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);

    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();
      this.#updateCharacterCount();

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }

      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Update radio button states
   * @param {string} mode - Current delivery mode
   */
  #updateButtonStates(mode) {
    const { DeliveryMode } = GiftVoucherRecipientForm;
    const isRecipientMode = mode === DeliveryMode.RECIPIENT;

    this.refs.myEmailButton.checked = !isRecipientMode;
    this.refs.recipientEmailButton.checked = isRecipientMode;
  }

  /**
   * Clear all recipient form fields
   */
  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorState();
  }

  /**
   * Disable recipient form fields when sending to self — disabled inputs post
   * no properties, so the order export falls back to the order email and the
   * customer's name.
   */
  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    this.#clearErrorState();
  }

  /**
   * Enable recipient form fields when sending to recipient
   */
  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
      if (field === this.refs.recipientEmail) {
        field.setAttribute('required', 'required');
      }
    }
  }

  /**
   * Update character count display
   */
  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    this.refs.characterCount.textContent = template
      .replace('[current]', currentLength.toString())
      .replace('[max]', maxLength.toString());
  }

  /**
   * Clear error styling and screen reader announcements
   */
  #clearErrorState() {
    if (this.refs.emailError) {
      this.refs.emailError.classList.add('hidden');
      const errorTextElement = this.refs.emailError.querySelector('span');
      if (errorTextElement) errorTextElement.textContent = '';
    }

    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }
}

// Register the custom element
customElements.define('gift-voucher-recipient-form', GiftVoucherRecipientForm);
